import React, { memo, useCallback, useState, useRef, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useProjectShipmentsCustomFields } from '@/core/api/queries/shipments';

export interface FilterSelectProps {
  label: string;
  fieldKey: string;
  onChange: (fieldKey: string, optionValue: string) => void;
  projectId: string | null;
  selectedValues: string[];
  selectedFilterCount: number;
}

export const FilterSelect: React.FC<FilterSelectProps> = memo(
  ({ label, fieldKey, onChange, projectId, selectedValues, selectedFilterCount }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const { data: options } = useProjectShipmentsCustomFields(projectId || '', fieldKey);

    const handleToggle = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    const handleSelect = useCallback(
      (optionValue: string) => {
        onChange(fieldKey, optionValue);
      },
      [onChange, fieldKey],
    );

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <div className="relative inline-block pr-2" ref={containerRef}>
        <button
          type="button"
          onClick={handleToggle}
          className="appearance-none pl-4 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-700 text-sm font-medium cursor-pointer hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-150 min-w-[80px] text-left"
          aria-label={label}
          aria-expanded={isOpen}
        >
          {label} {selectedFilterCount > 0 && `(${selectedFilterCount})`}
        </button>

        <div className="absolute inset-y-0 right-0 flex items-center pr-5 pointer-events-none">
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        {isOpen && (
          <div className="absolute z-50 mt-2 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <ul className="py-1 max-h-60 overflow-auto">
              {options?.length > 0 ? (
                options?.map((option: string) => {
                  const isSelected = selectedValues.includes(option);
                  return (
                    <li key={option}>
                      <button
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                          isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span>{option}</span>
                        {isSelected && <Check className="w-4 h-4 text-blue-700" />}
                      </button>
                    </li>
                  );
                })
              ) : (
                <li className="text-gray-500 px-4 py-2.5 text-sm">No options available</li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  },
);
